// US_06/US_05 — Xác thực report-GT (Gương Thần), API contract §1 & §1.1.
//
// Luồng (§1): SSO access token → `POST /auth/generate-token` → JWT report-GT.
// JWT nhận được được gắn làm `Authorization: Bearer <token>` mặc định trên
// `axiosGuongThanReportPrivate` — instance base API DÀNH RIÊNG cho `/api/reports/*`
// (đã có sẵn `X-Authorization` = api-key nội bộ, xem src/api/axios.ts §0.1).
// Cùng cơ chế token riêng biệt như G-Ticket/G-Process/G-Performance.
import { AxiosError, AxiosRequestConfig } from "axios";
import { axiosGuongThanReportPrivate } from "../../../api/axios";
import { AuthServiceInstance } from "../../auth/AuthService";
import {
  checkValidToken,
  setUrlRejectAfterLogin,
  withSsoAuthorization,
} from "../../../helpers/auth";
import { ReportAuthData, ReportEnvelope, ReportMeData } from "../types";
import { REPORT_AUTH_WS } from "./wsCode";

/** Key lưu JWT report-GT (tách khỏi `accessToken` account/SSO để không đè lẫn nhau). */
export const REPORT_GT_TOKEN_KEY = "reportGtToken";

/**
 * Gắn/gỡ JWT report-GT làm Authorization mặc định cho MỌI request `/api/reports/*`.
 * Tách riêng để login-flow (authSlice) và bootstrap (reload) tái sử dụng.
 */
export const setReportAuthToken = (token: string | null): void => {
  if (token) {
    axiosGuongThanReportPrivate.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token}`;
    localStorage.setItem(REPORT_GT_TOKEN_KEY, token);
  } else {
    delete axiosGuongThanReportPrivate.defaults.headers.common["Authorization"];
    localStorage.removeItem(REPORT_GT_TOKEN_KEY);
  }
};

/** Seed lại Authorization mặc định từ token đã lưu (dùng khi app reload trước khi login-flow xong). */
export const restoreReportAuthToken = (): void => {
  const token = localStorage.getItem(REPORT_GT_TOKEN_KEY);
  if (token) {
    axiosGuongThanReportPrivate.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token}`;
  }
};

/**
 * §1 — Đổi **SSO access token** lấy **JWT report-GT** rồi gắn vào `axiosGuongThanReportPrivate`.
 * `X-Authorization` (api-key) đã có sẵn trong instance; ở request NÀY ta gửi kèm
 * `Authorization: Bearer <SSO access token>` (KHÔNG phải JWT report-GT).
 * Trả về `data` (token, vai trò, hồ sơ NV) để caller điều hướng menu.
 */
export const generateReportToken = async (
  ssoAccessToken: string
): Promise<ReportAuthData> => {
  const { data } = await axiosGuongThanReportPrivate.post<ReportEnvelope<ReportAuthData>>(
    REPORT_AUTH_WS.generateToken,
    undefined,
    withSsoAuthorization(ssoAccessToken)
  );
  setReportAuthToken(data.data.token);
  return data.data;
};

/**
 * §1.1 — Vai trò TÍNH LIVE (`manager`/`staff`). Dùng khi vai trò đổi mà token
 * report-GT chưa hết hạn — không cần phát lại token. Dùng JWT report-GT đang gắn.
 */
export const getReportMe = async (): Promise<ReportMeData> => {
  const { data } = await axiosGuongThanReportPrivate.get<ReportEnvelope<ReportMeData>>(
    REPORT_AUTH_WS.me
  );
  return data.data;
};

/**
 * §1.2 — Lấy SSO access token còn hạn để đổi lấy token report-GT mới.
 * SSO server CHƯA hỗ trợ silent-renew, nên nếu token hết hạn/không có thì chuyển
 * hướng về luồng login (lưu lại URL hiện tại để quay lại sau khi đăng nhập) và
 * trả `null` — đúng cơ chế đang dùng ở App.tsx.
 */
const getValidSsoToken = async (): Promise<string | null> => {
  const user = await AuthServiceInstance.getUser();
  if (user && checkValidToken(user.access_token, user.expires_at)) {
    return user.access_token;
  }
  setUrlRejectAfterLogin(window.location.pathname + window.location.search);
  AuthServiceInstance.login();
  return null;
};

/**
 * Single-flight: nhiều request `/api/reports/*` chạy song song (vd
 * usePersonalReport gọi `Promise.all` 3 API) nếu cùng 401 sẽ chỉ phát LẠI token
 * report-GT MỘT lần rồi dùng chung kết quả.
 */
let refreshPromise: Promise<string | null> | null = null;

const refreshReportToken = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const ssoToken = await getValidSsoToken();
      if (!ssoToken) return null;
      const { token } = await generateReportToken(ssoToken);
      return token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

let interceptorInstalled = false;

/**
 * §1.2 — Tự phục hồi khi JWT report-GT hết hạn giữa phiên: bắt 401 trên
 * `axiosGuongThanReportPrivate`, phát lại token (đổi từ SSO access token) rồi
 * retry đúng request đó MỘT lần. Token report-GT không nằm trong luồng renew của
 * OIDC và app không có interceptor 401 nào khác, nên phải xử lý tại đây. Nếu SSO
 * token cũng hết hạn thì getValidSsoToken sẽ điều hướng về login.
 *
 * Tự cài NGAY khi module nạp (xem cuối file) — không cần wiring ở App.tsx.
 * `interceptorInstalled` chống cài trùng khi HMR/fast-refresh nạp lại module.
 */
const setupReportAuthInterceptor = (): void => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axiosGuongThanReportPrivate.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as
        | (AxiosRequestConfig & { _retry?: boolean })
        | undefined;

      const canRecover =
        error.response?.status === 401 &&
        !!config &&
        !config._retry &&
        // request đổi token cũng 401 ⇒ SSO token hỏng → không thể tự phục hồi.
        config.url !== REPORT_AUTH_WS.generateToken;

      if (canRecover && config) {
        config._retry = true;
        const newToken = await refreshReportToken();
        if (newToken) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return axiosGuongThanReportPrivate(config);
        }
      }

      return Promise.reject(error);
    }
  );
};

// Cài interceptor 401 ngay khi module được nạp (App bootstrap import module này
// qua restoreReportAuthToken, trước mọi request /api/reports/*).
setupReportAuthInterceptor();

const ReportAuthApi = {
  generateReportToken,
  getReportMe,
  setReportAuthToken,
  restoreReportAuthToken,
};

export default ReportAuthApi;